/**
 * Env-aware JWKS provider selector (capability `createJwksProviderFromEnv`,
 * request `91428c9a`; tracks drift `b02d1d9f`).
 *
 * Operator principle (2026-06-30): the S2S verifier must ALWAYS run — there is
 * no `NODE_ENV=test` / `skipAuth` bypass on the regression stack. Today the
 * JWKS-source selection (`S2S_AUTH_JWKS_URL ? remoteUrl : sigV4S3`) is
 * copy-pasted per service; that divergence — and the friction of a hardcoded
 * `createSigV4S3JWKSet` that can't reach a local/non-AWS stack — is exactly
 * what tempts a bypass. This factory pulls that selection into the lib ONCE so
 * every service makes a single call and none hand-rolls (or skips) auth.
 *
 * Fixed precedence (highest first):
 *   1. `opts.staticJwks` — UNIT TESTS ONLY. An explicit, PROGRAMMATIC in-memory
 *      JWKS so a test can mint a JWT with a local key and verify it offline.
 *      Deliberately NOT selectable from any env var: an env-driven static
 *      source would be precisely the auth-bypass vector the doctrine forbids
 *      (ops could pin a known key and skip real verification). It is opt-in
 *      from code, never from deployment config.
 *   2. `S2S_AUTH_JWKS_URL` — remote HTTP (`jose.createRemoteJWKSet`) for
 *      non-AWS / local stacks pointing at a published `/.well-known/jwks.json`.
 *   3. `S2S_AUTH_JWKS_S3_{BUCKET,REGION}` (+ optional `S2S_AUTH_JWKS_S3_KEY`,
 *      default `.well-known/jwks.json`) — SigV4-S3 (`createSigV4S3JWKSet`), the
 *      PROD default. That resolver already honors `AWS_ENDPOINT_URL_S3` /
 *      `AWS_ENDPOINT_URL` + path-style (drift `b02d1d9f`), so the SAME env-built
 *      provider reaches LocalStack/MinIO in the regression with NO bypass.
 *
 * When NOTHING is configured — or the S3 group is only partially set — the
 * factory THROWS. A missing JWKS source is a deploy misconfiguration that must
 * fail loud at boot, never degrade into a silent "skip auth".
 */
import type { RemoteJWKSetOptions } from "jose";
import { type JwksObject, createJwksProvider } from "./jwks-provider";
import type { GetKeyFn } from "./sigv4-s3-jwks";

/** Default S3 object key when `S2S_AUTH_JWKS_S3_KEY` is unset — the canonical well-known path. */
export const DEFAULT_JWKS_S3_KEY = ".well-known/jwks.json";

/** Env var names the selector reads. Exported so consumers / tests reference the canonical keys. */
export const JWKS_ENV_VARS = {
  remoteUrl: "S2S_AUTH_JWKS_URL",
  s3Bucket: "S2S_AUTH_JWKS_S3_BUCKET",
  s3Key: "S2S_AUTH_JWKS_S3_KEY",
  s3Region: "S2S_AUTH_JWKS_S3_REGION",
} as const;

export type JwksFromEnvOptions = {
  /**
   * Env source. Defaults to `process.env`. Pass an explicit map in tests to
   * avoid mutating the real process environment.
   */
  env?: Record<string, string | undefined>;
  /**
   * Explicit static JWKS — UNIT TESTS ONLY. Takes precedence over every env
   * var and builds an offline in-memory resolver (no network). NEVER wire this
   * from production config; it exists so a unit test can verify a
   * locally-minted JWT without standing up S3/HTTP. See the precedence note
   * above for why it is intentionally not env-selectable.
   */
  staticJwks?: JwksObject;
  /** Passed straight through to the remote-URL source (jose cooldown / cache / timeout tuning). */
  remoteOptions?: RemoteJWKSetOptions;
};

function trimmed(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

/**
 * Build the canonical `GetKeyFn` for the S2S verifier from the environment,
 * with the fixed precedence documented above. The result is wired exactly like
 * any other provider: `configureGrpcAuth({ jwksResolver })` or
 * `verifyS2SToken(token, opts)` after `setJwksResolver(...)`.
 *
 * @throws if no JWKS source is configured, or the SigV4-S3 group is partial —
 * a deploy misconfig must fail loud, never silently skip auth.
 *
 * @example Prod (SigV4-S3, the default):
 * ```ts
 * // env: S2S_AUTH_JWKS_S3_BUCKET=nodii-s2s-auth S2S_AUTH_JWKS_S3_REGION=ap-south-1
 * configureGrpcAuth({ jwksResolver: createJwksProviderFromEnv(), ... });
 * ```
 * @example Local / non-AWS stack (remote HTTP):
 * ```ts
 * // env: S2S_AUTH_JWKS_URL=http://auth:8080/.well-known/jwks.json
 * configureGrpcAuth({ jwksResolver: createJwksProviderFromEnv(), ... });
 * ```
 */
export function createJwksProviderFromEnv(
  opts: JwksFromEnvOptions = {},
): GetKeyFn {
  const env = opts.env ?? (process.env as Record<string, string | undefined>);

  // 1. Explicit static — programmatic, test-only. Never env-driven.
  if (opts.staticJwks !== undefined) {
    return createJwksProvider({ staticJwks: opts.staticJwks });
  }

  // 2. Remote HTTP — non-AWS / local stacks with a published JWKS endpoint.
  const url = trimmed(env[JWKS_ENV_VARS.remoteUrl]);
  if (url) {
    return createJwksProvider({
      remoteUrl: url,
      remoteOptions: opts.remoteOptions,
    });
  }

  // 3. SigV4-S3 — prod default. BUCKET + REGION are required (no sane default);
  //    KEY defaults to the canonical well-known path.
  const bucket = trimmed(env[JWKS_ENV_VARS.s3Bucket]);
  const region = trimmed(env[JWKS_ENV_VARS.s3Region]);
  const rawKey = trimmed(env[JWKS_ENV_VARS.s3Key]);
  if (bucket && region) {
    return createJwksProvider({
      sigV4S3: { region, bucket, key: rawKey ?? DEFAULT_JWKS_S3_KEY },
    });
  }

  // A partially-set S3 group is a misconfig, not "fall through to nothing".
  if (bucket || region || rawKey) {
    const missing = [
      !bucket && JWKS_ENV_VARS.s3Bucket,
      !region && JWKS_ENV_VARS.s3Region,
    ].filter(Boolean) as string[];
    throw new Error(
      `createJwksProviderFromEnv: incomplete SigV4-S3 config — set ${missing.join(", ")} (${JWKS_ENV_VARS.s3Key} defaults to "${DEFAULT_JWKS_S3_KEY}")`,
    );
  }

  // Nothing configured → fail LOUD. The verifier must always run; a missing
  // JWKS source is a deploy misconfig, never a silent auth bypass.
  throw new Error(
    `createJwksProviderFromEnv: no JWKS source configured — set ${JWKS_ENV_VARS.remoteUrl} (remote HTTP, non-AWS/local) or ${JWKS_ENV_VARS.s3Bucket} + ${JWKS_ENV_VARS.s3Region} (SigV4-S3, prod default). The verifier must always run; a missing JWKS source is a deploy misconfiguration, not an auth bypass.`,
  );
}
